import React, { useEffect, useRef, useState } from "react";
import { fetchServices } from "../lib/api";
import {
  Sparkles, Sun, Layers, AlignCenter, Anchor, Circle, Shield,
  Heart, Grid,
} from "lucide-react";
import { Jaali, BorderOrnament, Paisley } from "./IndianMotifs";

const ICONS = {
  sparkles: Sparkles, sun: Sun, layers: Layers, "align-center": AlignCenter,
  anchor: Anchor, circle: Circle, shield: Shield,
  heart: Heart, grid: Grid,
};

// Image map for each service — fills the visual void in the cards
const SERVICE_IMAGES = {
  comprehensive: { src: "/img/service-comprehensive.png", alt: "Modern dental treatment room with warm cream tones and sage-green accents" },
  family: { src: "/img/service-family.png", alt: "Father and daughter smiling together in a warm dental clinic waiting area" },
  cosmetic: { src: "/img/service-cosmetic.png", alt: "Close-up of a radiant, confident smile after cosmetic dental treatment" },
  tech: { src: "/img/service-technology.png", alt: "Digital intraoral scanner in use during a modern dental procedure" },
  calm: { src: "/img/service-calm.png", alt: "Serene, spa-like dental treatment room with soft natural light and calming decor" },
  convenient: { src: "/img/service-appointments.png", alt: "Welcoming dental clinic reception area with warm wood accents and gold lighting" },
};

function TiltCard({ children, ...rest }) {
  const ref = useRef(null);
  const onMove = (e) => {
    // WCAG 2.3.3: check reduced motion
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(900px) rotateX(${-y * 8}deg) rotateY(${x * 8}deg) translateY(-4px)`;
    el.style.boxShadow = `0 30px 60px -30px rgba(56,79,49,0.35), 0 0 0 1px rgba(201,169,110,${0.15 + Math.abs(x) * 0.25})`;
  };
  const reset = () => {
    const el = ref.current; if (!el) return;
    el.style.transform = "perspective(900px) rotateX(0) rotateY(0) translateY(0)";
    el.style.boxShadow = "0 12px 30px -20px rgba(56,79,49,0.2)";
  };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className="tilt-card rounded-3xl" {...rest}>
      {children}
    </div>
  );
}

function ServiceCard({ s, idx, onLearn }) {
  const Icon = ICONS[s.icon] || Sparkles;
  const img = SERVICE_IMAGES[s.id];
  return (
    <TiltCard data-testid={`service-card-${s.id}`} data-cursor="hover">
      <button
        type="button"
        onClick={() => onLearn(s)}
        data-testid={`service-learn-${s.id}`}
        aria-label={`Learn more about ${s.name}`}
        className="block w-full text-left relative h-full rounded-3xl bg-white/80 backdrop-blur border border-sage-700/10 overflow-hidden focus-visible:ring-2 focus-visible:ring-gold-400"
      >
        {/* Service image — fills the empty visual space */}
        {img && (
          <div className="overflow-hidden">
            <img
              src={img.src}
              alt={img.alt}
              loading="lazy"
              className="service-card-img"
            />
          </div>
        )}

        <div className="p-7 md:p-8">
          <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-gold-100/60 blur-2xl" aria-hidden="true" />
          <div className="flex items-start justify-between">
            <div className="grid place-items-center w-14 h-14 rounded-2xl bg-sage-700/8 text-sage-700" aria-hidden="true">
              <Icon className="w-6 h-6" strokeWidth={1.4} aria-hidden="true" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-sage-700/60" aria-hidden="true">Dental · {String(idx + 1).padStart(2, "0")}</div>
          </div>
          <h3 className="mt-7 font-display text-2xl md:text-[26px] leading-tight text-ink">{s.name}</h3>
          <p className="mt-3 text-sm text-ink/65 leading-relaxed">{s.desc}</p>
          <div className="mt-7 flex items-center justify-between text-[11px] uppercase tracking-[0.28em]" aria-hidden="true">
            <span className="text-sage-700/70 group-hover:text-gold-600 transition-colors">Learn more</span>
            <span className="grid place-items-center w-8 h-8 rounded-full border border-sage-700/30 text-sage-700 transition-all duration-300 group-hover:bg-sage-800 group-hover:text-cream group-hover:border-sage-800">→</span>
          </div>
        </div>
      </button>
    </TiltCard>
  );
}

export default function Services({ onBook, onLearn }) {
  const [list, setList] = useState([]);
  const root = useRef(null);

  useEffect(() => {
    fetchServices().then((data) => setList(data.dental || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const els = root.current?.querySelectorAll(".reveal") || [];
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("in"); });
    }, { threshold: 0.15 });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [list]);

  return (
    <section id="services" ref={root} aria-label="Our dental services" className="relative py-28 md:py-40 bg-bone overflow-hidden">
      <Jaali size={320} className="absolute -top-10 -right-10 text-sage-700" opacity={0.08} aria-hidden="true" />
      <Jaali size={260} className="absolute -bottom-10 -left-10 text-gold-600" opacity={0.10} aria-hidden="true" />
      <Paisley size={120} className="absolute top-1/2 right-8 text-gold-500/15 hidden xl:block" aria-hidden="true" />
      <div className="container mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.32em] text-sage-700/80 mb-3">Services — 02</div>
            <h2 className="font-display text-5xl md:text-6xl xl:text-7xl leading-[1.02] tracking-tightest">
              Dentistry, <span className="font-script text-gold-500">folded</span><br />into simple, careful steps.
            </h2>
            <div className="mt-4 text-gold-600/70" aria-hidden="true"><BorderOrnament width={220} /></div>
            <p className="mt-6 max-w-xl text-ink/70 leading-relaxed text-lg">
              Like a single sheet becoming something whole, every treatment we offer is shaped from the same belief: care should feel quiet, deliberate, and unhurried.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" role="list">
          {list.map((s, i) => (
            <div key={s.id} className="reveal group" role="listitem" style={{ transitionDelay: `${(i % 6) * 90}ms` }}>
              <ServiceCard s={s} idx={i} onLearn={onLearn} />
            </div>
          ))}
        </div>

        <div className="mt-16 flex flex-col md:flex-row items-center justify-between gap-6 reveal">
          <p className="font-display text-2xl md:text-3xl text-sage-900 max-w-xl">
            Not sure where to start? Let's build a plan just for you.
          </p>
          <button
            onClick={onBook}
            data-testid="services-book-btn"
            data-cursor="hover"
            className="inline-flex items-center gap-3 rounded-full bg-gold-500 text-sage-900 pl-6 pr-2 py-2 btn-glow"
          >
            <span className="uppercase text-[12px] tracking-[0.28em]">Plan my smile</span>
            <span className="grid place-items-center w-10 h-10 rounded-full bg-sage-900 text-cream" aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </section>
  );
}
